import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Form, InputGroup, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaSearch, FaEye, FaTrash } from 'react-icons/fa';
import axios from 'axios';

const BestellungListe = () => {
  const [bestellungen, setBestellungen] = useState([]);
  const [filteredBestellungen, setFilteredBestellungen] = useState([]);
  const [suchbegriff, setSuchbegriff] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Status-Farben für die Badges
  const statusVariants = {
    'ausstehend': 'warning',
    'bestellt': 'info',
    'geliefert': 'success',
    'storniert': 'danger'
  };

  // Bestellungen vom Backend laden
  const loadBestellungen = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/bestellungen');
      setBestellungen(response.data);
      setFilteredBestellungen(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Bestellungen:', err);
      setError('Die Bestellungen konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Beim ersten Rendern Bestellungen laden
  useEffect(() => {
    loadBestellungen();
  }, []);

  // Bestellungen filtern basierend auf Suchbegriff
  useEffect(() => {
    if (suchbegriff.trim() === '') {
      setFilteredBestellungen(bestellungen);
    } else {
      const lowerSuchbegriff = suchbegriff.toLowerCase();
      const filtered = bestellungen.filter(bestellung => 
        bestellung.bestellnummer.toLowerCase().includes(lowerSuchbegriff) ||
        bestellung.status.toLowerCase().includes(lowerSuchbegriff)
      );
      setFilteredBestellungen(filtered);
    }
  }, [suchbegriff, bestellungen]);

  // Bestellung löschen
  const handleDelete = async (id, bestellnummer) => {
    if (window.confirm(`Möchten Sie die Bestellung "${bestellnummer}" wirklich löschen?`)) {
      try {
        await axios.delete(`/api/bestellungen/${id}`);
        loadBestellungen(); // Liste neu laden
      } catch (err) {
        console.error('Fehler beim Löschen der Bestellung:', err);
        setError(`Die Bestellung "${bestellnummer}" konnte nicht gelöscht werden.`);
      }
    }
  };

  // Datum formatieren
  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('de-DE', options);
  };

  return (
    <Container>
      <h1 className="mb-4">Bestellungen</h1>
      
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      
      <Row className="mb-4">
        <Col md={6} className="mb-3 mb-md-0">
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Bestellungen suchen..."
              value={suchbegriff}
              onChange={(e) => setSuchbegriff(e.target.value)}
            />
          </InputGroup>
        </Col>
      </Row>
      
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Laden...</span>
          </Spinner>
          <p className="mt-2">Bestellungen werden geladen...</p>
        </div>
      ) : filteredBestellungen.length === 0 ? (
        <Card className="text-center p-4 shadow-sm">
          <Card.Body>
            <h5>Keine Bestellungen gefunden</h5>
            <p className="text-muted">
              {suchbegriff ? 
                `Es wurden keine Bestellungen gefunden, die "${suchbegriff}" enthalten.` : 
                'Es sind noch keine Bestellungen im System erfasst.'}
            </p>
            <Button 
              as={Link} 
              to="/warenkorb" 
              variant="primary"
            >
              Zum Warenkorb
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Bestellnummer</th>
                    <th>Datum</th>
                    <th>Status</th>
                    <th>Gesamtpreis</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBestellungen.map(bestellung => (
                    <tr key={bestellung.id}>
                      <td>
                        <Link to={`/bestellungen/${bestellung.id}`} className="text-decoration-none fw-bold">
                          {bestellung.bestellnummer}
                        </Link>
                      </td>
                      <td>{formatDate(bestellung.datum)}</td>
                      <td>
                        <Badge bg={statusVariants[bestellung.status] || 'secondary'}>
                          {bestellung.status.charAt(0).toUpperCase() + bestellung.status.slice(1)}
                        </Badge>
                      </td>
                      <td>{bestellung.gesamtpreis.toFixed(2)} €</td>
                      <td>
                        <Button 
                          as={Link} 
                          to={`/bestellungen/${bestellung.id}`} 
                          variant="outline-primary" 
                          size="sm" 
                          className="me-1 mb-1 mb-md-0" 
                          title="Bestellung anzeigen"
                        >
                          <FaEye />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          className="mb-1 mb-md-0" 
                          title="Bestellung löschen"
                          onClick={() => handleDelete(bestellung.id, bestellung.bestellnummer)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          <Card.Footer className="bg-white">
            <small className="text-muted">
              {filteredBestellungen.length} {filteredBestellungen.length === 1 ? 'Bestellung' : 'Bestellungen'} gefunden
            </small>
          </Card.Footer>
        </Card>
      )}
    </Container>
  );
};

export default BestellungListe;
